import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAssetContext } from '@/context/AssetContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BookOpen,
  Headphones,
  PenTool,
  Mic,
  Trophy,
  Target,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  BarChart3,
  Calendar,
  Home
} from 'lucide-react';

const API_URL = window.location.origin;

const TestResultsDashboard = () => {
  const { isDark } = useTheme();
  const { getLogo } = useAssetContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState(searchParams.get('test') || 'ielts');
  const [overallResults, setOverallResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResults();
  }, [activeTab]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      // Fetch overall results
      const overallRes = await fetch(`${API_URL}/api/english-test/overall-results?test_type=${activeTab}`);
      const overallData = await overallRes.json();
      if (overallData.success) {
        setOverallResults(overallData.results);
      }
      
      // Fetch history
      const historyRes = await fetch(`${API_URL}/api/english-test/history?test_type=${activeTab}&limit=10`);
      const historyData = await historyRes.json();
      if (historyData.success) {
        setHistory(historyData.history);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSectionIcon = (section) => {
    const icons = {
      reading: BookOpen,
      listening: Headphones,
      writing: PenTool,
      speaking: Mic,
      speaking_writing: Mic
    };
    const Icon = icons[section] || BookOpen;
    return <Icon className="w-5 h-5" />;
  };

  const getSectionColor = (section) => {
    const colors = {
      reading: { bg: 'bg-blue-500', text: 'text-blue-500', light: 'bg-blue-500/10' },
      listening: { bg: 'bg-purple-500', text: 'text-purple-500', light: 'bg-purple-500/10' },
      writing: { bg: 'bg-green-500', text: 'text-green-500', light: 'bg-green-500/10' },
      speaking: { bg: 'bg-orange-500', text: 'text-orange-500', light: 'bg-orange-500/10' },
      speaking_writing: { bg: 'bg-pink-500', text: 'text-pink-500', light: 'bg-pink-500/10' }
    };
    return colors[section] || colors.reading;
  };

  const getBandDescription = (band, testType) => {
    if (testType === 'ielts') {
      if (band >= 8) return 'Expert User';
      if (band >= 7) return 'Good User';
      if (band >= 6) return 'Competent User';
      if (band >= 5) return 'Modest User';
      return 'Limited User';
    }
    // PTE
    if (band >= 76) return 'Expert';
    if (band >= 59) return 'Proficient';
    if (band >= 43) return 'Competent';
    return 'Developing';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // Backend stores dates in UTC, append 'Z' to ensure proper parsing as UTC
    const utcDateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    return new Date(utcDateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const sections = activeTab === 'ielts' 
    ? ['reading', 'listening', 'writing', 'speaking']
    : ['reading', 'listening', 'speaking_writing'];

  return (
    <div className={`min-h-screen pt-20 pb-12 ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
      <div className="max-w-6xl mx-auto px-4">
        {/* Top Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/uac-desk/english-test')}
            data-testid="back-to-tests-btn"
            className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Tests
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/uac-desk/dashboard')}
            data-testid="exit-to-dashboard-btn"
            className={`border-red-500/50 text-red-500 hover:bg-red-500/10 hover:text-red-400`}
          >
            <Home className="w-4 h-4 mr-1" /> Exit to Dashboard
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src={getLogo()} 
            alt="UniXperts" 
            className={`h-12 mx-auto mb-4 ${isDark ? 'brightness-0 invert' : ''}`}
          />
          <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Your Test Results
          </h1>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Track your progress across all English proficiency test sections
          </p>
        </div>

        {/* Test Type Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className={`grid w-full max-w-md mx-auto grid-cols-2 ${isDark ? 'bg-[#233842]' : ''}`}>
            <TabsTrigger value="ielts" className={isDark ? 'data-[state=active]:bg-blue-500' : ''}>
              IELTS Academic
            </TabsTrigger>
            <TabsTrigger value="pte" className={isDark ? 'data-[state=active]:bg-green-500' : ''}>
              PTE Academic
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className={`w-8 h-8 animate-spin ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </div>
        ) : (
          <>
            {/* Overall Score Card */}
            {overallResults && (
              <Card className={`mb-8 ${isDark ? 'bg-gradient-to-br from-[#233842] to-[#1a2830] border-white/10' : 'bg-gradient-to-br from-white to-gray-50'}`}>
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-3 gap-8">
                    {/* Overall Band Score */}
                    <div className="text-center">
                      <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full mb-4 ${
                        isDark ? 'bg-gradient-to-br from-amber-500/20 to-yellow-500/20 border-2 border-amber-500/30' : 'bg-gradient-to-br from-amber-100 to-yellow-100 border-2 border-amber-300'
                      }`}>
                        <div className="text-center">
                          <Trophy className={`w-8 h-8 mx-auto mb-1 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                          <span className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {overallResults.overall_band || '-'}
                          </span>
                        </div>
                      </div>
                      <h3 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Overall {activeTab === 'ielts' ? 'Band' : 'Score'}
                      </h3>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {overallResults.overall_band ? getBandDescription(overallResults.overall_band, activeTab) : 'Complete tests to see your score'}
                      </p>
                    </div>

                    {/* Progress */}
                    <div>
                      <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Completion Progress
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                            Sections Completed
                          </span>
                          <span className={`font-medium ${isDark ? 'text-white' : ''}`}>
                            {overallResults.completed_sections} / {overallResults.total_sections}
                          </span>
                        </div>
                        <Progress 
                          value={(overallResults.completed_sections / overallResults.total_sections) * 100} 
                          className="h-3"
                        />
                        {overallResults.completed_sections < overallResults.total_sections && (
                          <p className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                            Complete all sections for a full assessment
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div>
                      <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Quick Stats
                      </h4>
                      <div className="space-y-3">
                        <div className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                          <Target className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                          <div>
                            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Average Score</div>
                            <div className={`font-semibold ${isDark ? 'text-white' : ''}`}>
                              {overallResults.overall_score ? `${overallResults.overall_score}%` : 'N/A'}
                            </div>
                          </div>
                        </div>
                        <div className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                          <BarChart3 className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                          <div>
                            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Tests Taken</div>
                            <div className={`font-semibold ${isDark ? 'text-white' : ''}`}>
                              {history.length}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Section Scores */}
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Section Breakdown
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {sections.map((section) => {
                const sectionData = overallResults?.sections?.[section] || {};
                const colors = getSectionColor(section);
                const isCompleted = sectionData.score !== null && sectionData.score !== undefined;
                
                // Get band score - use stored band or calculate from percentage
                const bandScore = sectionData.band || (isCompleted ? (4 + (sectionData.score / 100) * 5) : null);
                
                return (
                  <Card 
                    key={section} 
                    className={`${isDark ? 'bg-[#233842] border-white/10' : ''} ${
                      isCompleted ? '' : 'opacity-60'
                    }`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`p-2 rounded-lg ${colors.light}`}>
                          <span className={colors.text}>{getSectionIcon(section)}</span>
                        </div>
                        <div>
                          <h3 className={`font-semibold capitalize ${isDark ? 'text-white' : ''}`}>
                            {section.replace('_', ' & ')}
                          </h3>
                          {isCompleted && bandScore && (
                            <Badge variant="outline" className={`text-xs ${colors.text} border-current`}>
                              {activeTab === 'pte' && sectionData.points 
                                ? `${sectionData.points} pts` 
                                : `Band ${typeof bandScore === 'number' ? bandScore.toFixed(1) : bandScore}`}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {isCompleted ? (
                        <>
                          <div className="mb-3">
                            <div className="flex justify-between text-sm mb-1">
                              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Score</span>
                              <span className={`font-medium ${isDark ? 'text-white' : ''}`}>
                                {Math.round(sectionData.score)}%
                              </span>
                            </div>
                            <Progress value={sectionData.score} className="h-2" />
                          </div>
                          <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                            {sectionData.correct_answers}/{sectionData.total_questions} correct
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            <Clock className="w-3 h-3 inline mr-1" />
                            {formatDate(sectionData.completed_at)}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <XCircle className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Not attempted yet
                          </p>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => navigate(`/uac-desk/english-test/${activeTab}/${section}`)}
                            className="mt-2"
                          >
                            Take Test
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Recommendations */}
            {overallResults?.recommendations?.length > 0 && (
              <Card className={`mb-8 ${isDark ? 'bg-[#233842] border-white/10' : ''}`}>
                <CardHeader>
                  <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    Personalized Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {overallResults.recommendations.map((rec, idx) => (
                      <li key={idx} className={`flex items-start gap-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Test History */}
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Recent Test History
            </h2>
            {history.length > 0 ? (
              <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
                <CardContent className="p-0">
                  <div className="divide-y divide-gray-200 dark:divide-white/10">
                    {history.map((item) => {
                      const colors = getSectionColor(item.section);
                      const displayScore = item.band_score 
                        ? `Band ${item.band_score}` 
                        : item.points 
                        ? `${item.points} pts`
                        : `${Math.round(item.score || 0)}%`;
                      
                      return (
                        <div 
                          key={item.id} 
                          className={`p-4 flex items-center justify-between hover:${isDark ? 'bg-white/5' : 'bg-gray-50'}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${colors.light}`}>
                              <span className={colors.text}>{getSectionIcon(item.section)}</span>
                            </div>
                            <div>
                              <div className={`font-medium ${isDark ? 'text-white' : ''}`}>
                                {item.test_type.toUpperCase()} - {item.section.replace('_', ' & ').charAt(0).toUpperCase() + item.section.replace('_', ' & ').slice(1)}
                              </div>
                              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                {formatDate(item.completed_at)}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-2xl font-bold ${
                              (item.band_score >= 7 || item.points >= 65 || item.score >= 70) 
                                ? 'text-green-500' 
                                : (item.band_score >= 5.5 || item.points >= 50 || item.score >= 50)
                                ? 'text-amber-500'
                                : isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                              {displayScore}
                            </div>
                            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {item.correct_answers}/{item.total_questions} correct
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
                <CardContent className="p-8 text-center">
                  <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : ''}`}>
                    No Tests Completed Yet
                  </h3>
                  <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Take your first {activeTab.toUpperCase()} practice test to see your results here.
                  </p>
                  <Button onClick={() => navigate('/uac-desk/english-test')}>
                    Start Practice Test <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="flex justify-center gap-4 mt-8">
              <Button 
                variant="outline" 
                onClick={() => navigate('/uac-desk/english-test')}
                className={isDark ? 'border-white/20' : ''}
              >
                Back to Tests
              </Button>
              <Button onClick={() => navigate('/uac-desk/english-test')}>
                Take Another Test <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TestResultsDashboard;
